import {useQueryClient, useSuspenseQuery} from '@github-ui/react-query'
import {createPersister} from '@github-ui/react-query-persister'
import {useAnalytics} from '@github-ui/use-analytics'
import {useMemo, useCallback} from 'react'
import safeStorage from '@github-ui/safe-storage'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {Type} from '@github-ui/react-query-persister/typebox'
import {reactFetch} from '@github-ui/verified-fetch'
import {useCurrentRepository} from '@github-ui/current-repository'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import {buildCodespacesPath} from '../build-codespaces-path'
import {isCachingEnabled} from './flags'

const safeLocalStorage = safeStorage('localStorage')

// Schema for caching Codespaces tab HTML content
const CodespacesContentSchema = Type.String()
const CodespacesContentValidator = Compile(CodespacesContentSchema)

async function fetchCodespacesContent(codespacesPath?: string) {
  if (!codespacesPath) return ''

  const result = await reactFetch(codespacesPath)
  if (result.status >= 400 && result.status <= 499) {
    return ''
  } else if (!result.ok) {
    throw new Error(`HTTP ${result.status}`)
  }
  return await result.text()
}

const useCodespaceTabContentQueryOptions = (codespacesPathProp?: string) => {
  const repo = useCurrentRepository()
  const refInfo = useRefInfo()
  const codespacesPath =
    codespacesPathProp || (repo && refInfo ? buildCodespacesPath(repo.id, refInfo.name) : undefined)
  const {sendAnalyticsEvent} = useAnalytics()

  return useMemo(
    () => ({
      queryKey: ['CodespacesTabContent.content', codespacesPath],
      queryFn: () => fetchCodespacesContent(codespacesPath),
      persister: createPersister({
        validator: CodespacesContentValidator,
        sendAnalyticsEvent,
        enabled: isCachingEnabled(),
      }),
    }),
    [codespacesPath, sendAnalyticsEvent],
  )
}

export function usePrefetchCodespacesQuery(codespacesPath?: string) {
  const queryClient = useQueryClient()
  const codespaceQueryOptions = useCodespaceTabContentQueryOptions(codespacesPath)

  return useCallback(() => {
    const isEnabled = isCachingEnabled()
    const defaultTab = safeLocalStorage.getItem('code-button-default-tab')

    if (!isEnabled || defaultTab !== 'cloud') {
      return
    }

    return queryClient.prefetchQuery(codespaceQueryOptions)
  }, [queryClient, codespaceQueryOptions])
}

export function useCodespaceContent(codespacesPath?: string) {
  const codespaceQueryOptions = useCodespaceTabContentQueryOptions(codespacesPath)
  return useSuspenseQuery(codespaceQueryOptions)
}
